import React, { useRef, type KeyboardEvent, type ClipboardEvent } from "react";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import { useField } from "formik";
import { TextInput } from "@jsq/ds/core";
import {
  GIIN_PLACEHOLDERS,
  GIIN_SEGMENT_LABELS,
  GIIN_SEGMENT_LENGTHS,
  GIIN_SEGMENT_WIDTHS,
} from "./constants";
import { container, segmentWidth, segmentWrapper, separator } from "./styles_vanilla.css";
import { sanitizeGiinValue, joinSegments, findLastFilledIndex } from "./utils";

type GiinFieldProps = {
  name: string;
  disabled?: boolean;
  "data-testid"?: string;
};

export const GiinField = ({ name, disabled, "data-testid": testId }: GiinFieldProps) => {
  const [field, , helpers] = useField<string>(name);
  const containerRef = useRef<HTMLDivElement>(null);

  const segments = (field.value || "").split(".");
  const segmentValues = GIIN_SEGMENT_LENGTHS.map((_, i) => segments[i] || "");

  const getInputs = () => containerRef.current?.querySelectorAll("input");

  const handleSegmentChange = (index: number, value: string) => {
    const sanitizedValue = sanitizeGiinValue(value);
    const newSegments = [...segmentValues];
    newSegments[index] = sanitizedValue.slice(0, GIIN_SEGMENT_LENGTHS[index]);
    void helpers.setValue(joinSegments(newSegments));

    if (sanitizedValue.length >= GIIN_SEGMENT_LENGTHS[index]) {
      getInputs()?.[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !segmentValues[index] && index > 0) {
      getInputs()?.[index - 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text/plain");
    const rawChars = sanitizeGiinValue(pastedData);
    if (!rawChars) return;

    const inputs = getInputs();
    const targetIndex = inputs
      ? Array.from(inputs).findIndex((input) => input === e.target)
      : -1;
    const startIndex = targetIndex >= 0 ? targetIndex : 0;

    const newSegments = [...segmentValues];
    let currentPos = 0;
    for (let i = startIndex; i < GIIN_SEGMENT_LENGTHS.length; i++) {
      const len = GIIN_SEGMENT_LENGTHS[i];
      newSegments[i] = rawChars.slice(currentPos, currentPos + len);
      currentPos += len;
    }

    void helpers.setValue(joinSegments(newSegments));

    const lastFilledIndex = findLastFilledIndex(newSegments);
    inputs?.[lastFilledIndex === -1 ? startIndex : lastFilledIndex]?.focus();
  };

  return (
    <div ref={containerRef} className={container} onPaste={handlePaste} data-testid={testId}>
      {GIIN_SEGMENT_LENGTHS.map((maxLength, index) => (
        <React.Fragment key={index}>
          <div
            className={segmentWrapper}
            style={assignInlineVars({ [segmentWidth]: GIIN_SEGMENT_WIDTHS[index] })}
          >
            <TextInput
              value={segmentValues[index]}
              onSetValue={(value) => handleSegmentChange(index, value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              placeholder={GIIN_PLACEHOLDERS[index]}
              maxLength={maxLength}
              disabled={disabled}
              aria-label={GIIN_SEGMENT_LABELS[index]}
              data-testid={`${testId}-segment-${index}`}
              isFullWidth
            />
          </div>
          {index < GIIN_SEGMENT_LENGTHS.length - 1 && (
            <span className={separator} aria-hidden="true">
              .
            </span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};
